import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { cmsApiConfig } from '../../cmsConfig';
import {
  ApplicationDocumentModel,
  ApplicationModel,
  ApplicationType,
  LatestIncidentBySsnMatchOutcome,
  NonQualifyingPeriodType,
  NonQualifyingPeriodTypeEnum,
} from '../../types/ApplicationV3/application';
import {
  toBackendApplicationType,
  toFrontendApplicationType,
} from './applicationTypeMapper';
import {
  ApplicationFullTexts,
  ApplicationStartTexts,
  ConfirmationTexts,
} from '../../types/ApplicationV3/applicationTexts';
import { SaveFormResponse } from '../../types/Application/application';

export enum ApplicationSubmitMode {
  Create = 0,
  Complete = 1,
}
export type ApplicationSubmitRequest = ApplicationModel & {
  /**
   * Forward-compatible discriminator so backend can branch explicitly between
   * create vs completion/update flows without relying on inference.
   */
  submitMode?: ApplicationSubmitMode;
};

type GetLatestIncidentBySsnApiResponse = {
  isSuccess?: boolean;
  data?: ApplicationModel | null;
  errorMessage?: string | null;
  statusCode?: number;
  matchOutcome?: LatestIncidentBySsnMatchOutcome;
};

export type GetLatestIncidentBySsnResult = {
  application: ApplicationModel | null;
  statusCode: number;
  matchOutcome?: LatestIncidentBySsnMatchOutcome;
};

type ApplicationSubmitError = {
  status: number | string;
  message?: string;
};

const extractApiErrorMessage = (payload: unknown): string | undefined => {
  if (!payload || typeof payload !== 'object') {
    return undefined;
  }

  const value = payload as {
    message?: string;
    Message?: string;
    detail?: string;
    Detail?: string;
    title?: string;
    Title?: string;
  };

  const candidate =
    value.message ??
    value.Message ??
    value.detail ??
    value.Detail ??
    value.title ??
    value.Title;

  if (typeof candidate === 'string' && candidate.trim().length > 0) {
    return candidate;
  }

  return undefined;
};

const normalizeNonQualifyingPeriodType = (
  raw: unknown
): NonQualifyingPeriodType | undefined => {
  const parsed =
    typeof raw === 'number'
      ? raw
      : typeof raw === 'string'
        ? Number(raw)
        : Number.NaN;

  switch (parsed) {
    case NonQualifyingPeriodTypeEnum.SickLeave:
      return NonQualifyingPeriodTypeEnum.SickLeave;
    case NonQualifyingPeriodTypeEnum.TotalDefenseDuty:
      return NonQualifyingPeriodTypeEnum.TotalDefenseDuty;
    case NonQualifyingPeriodTypeEnum.ParentalLeave:
      return NonQualifyingPeriodTypeEnum.ParentalLeave;
    case NonQualifyingPeriodTypeEnum.StudiesWithAdjustmentAid:
      return NonQualifyingPeriodTypeEnum.StudiesWithAdjustmentAid;
    default:
      return undefined;
  }
};

const normalizeNullableDate = (
  value: string | undefined | null
): string | null => {
  const trimmed = (value ?? '').trim();
  return trimmed.length > 0 ? trimmed : null;
};

export const applicationApi = createApi({
  reducerPath: 'applicationApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${cmsApiConfig.url}/application`,
  }),
  endpoints: (builder) => ({
    getStartTexts: builder.query<ApplicationStartTexts, void>({
      query: () => '/startTexts',
    }),
    getFullTexts: builder.query<ApplicationFullTexts, void>({
      query: () => ({ url: '/fullTexts', method: 'GET', cache: 'default' }),
    }),

    getLatestIncidentBySsn: builder.mutation<
      GetLatestIncidentBySsnResult,
      string
    >({
      query: (ssn) => ({
        url: '/getLatestApplicationBySsn',
        method: 'POST',
        body: JSON.stringify(ssn),
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (
        res: GetLatestIncidentBySsnApiResponse
      ): GetLatestIncidentBySsnResult => {
        const rawApplication = (res.data ?? null) as ApplicationModel | null;
        const rawApplicationType = (rawApplication as any)?.applicationType;
        const mappedApplicationType =
          toFrontendApplicationType(rawApplicationType);
        const mappedNonQualifyingPeriods =
          rawApplication?.nonQualifyingPeriods.map((period) => ({
            ...period,
            nonQualifyingPeriodType: normalizeNonQualifyingPeriodType(
              period.nonQualifyingPeriodType
            ),
          }));
        const application = rawApplication
          ? ({
              ...rawApplication,
              applicationType: mappedApplicationType ?? rawApplicationType,
              nonQualifyingPeriods: mappedNonQualifyingPeriods,
            } as ApplicationModel)
          : null;
        const statusCode = (res.statusCode ?? 0) as number;
        const result: GetLatestIncidentBySsnResult = {
          application,
          statusCode,
          matchOutcome: res.matchOutcome,
        };
        return result;
      },
      transformErrorResponse: (res) => {
        const payload = res.data;
        const error: ApplicationSubmitError = {
          status: res.status,
          message: extractApiErrorMessage(payload),
        };
        return error;
      },
    }),

    getScriveConfirmationTexts: builder.query<
      ConfirmationTexts,
      ApplicationType
    >({
      query: (applicationType) => {
        const backendApplicationType =
          toBackendApplicationType(applicationType);
        return `/scriveConfirmationTexts?applicationType=${backendApplicationType}`;
      },
    }),
    getApplicationDocument: builder.mutation<Blob, ApplicationDocumentModel>({
      query: (applicationDocumentModel) => ({
        url: '/getApplicationSigningDocument',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: {
          ...applicationDocumentModel,
          employmentPeriods: applicationDocumentModel.employmentPeriods.map(
            (period) => ({
              ...period,
              // Backend expects nullable DateTime for these fields. Empty string fails model binding.
              noticeDate: period.noticeDate || null,
              endDate: period.endDate || null,
            })
          ),
          applicationType: toBackendApplicationType(
            applicationDocumentModel.applicationType
          ),
        },
        responseHandler: async (response) => await response.blob(),
      }),
    }),

    createApplication: builder.mutation<
      SaveFormResponse,
      ApplicationSubmitRequest
    >({
      query: (formData) => ({
        url: '/saveForm',
        method: 'POST',
        body: {
          ...formData,
          employmentPeriods: (formData.employmentPeriods ?? []).map(
            (period) => ({
              ...period,
              noticeDate: normalizeNullableDate(period.noticeDate),
              endDate: normalizeNullableDate(period.endDate),
            })
          ),
          incidentId:
            typeof formData.incidentId === 'string' &&
            formData.incidentId.trim().length > 0
              ? formData.incidentId
              : null,
          applicationType: toBackendApplicationType(formData.applicationType),
          submitMode: ApplicationSubmitMode.Create,
        },
      }),
      transformResponse: (res: SaveFormResponse) => {
        if ('signingUrl' in res) {
          return { statusCode: res.statusCode, signingUrl: res.signingUrl };
        } else {
          return { statusCode: res.statusCode };
        }
      },
      transformErrorResponse: (res) => {
        const payload = res.data;
        return {
          status: res.status,
          message: extractApiErrorMessage(payload),
        } as ApplicationSubmitError;
      },
    }),

    completeApplication: builder.mutation<
      SaveFormResponse,
      ApplicationSubmitRequest
    >({
      query: (formData) => ({
        url: '/saveForm',
        method: 'POST',
        body: {
          ...formData,
          employmentPeriods: (formData.employmentPeriods ?? []).map(
            (period) => ({
              ...period,
              noticeDate: normalizeNullableDate(period.noticeDate),
              endDate: normalizeNullableDate(period.endDate),
            })
          ),
          incidentId:
            typeof formData.incidentId === 'string' &&
            formData.incidentId.trim().length > 0
              ? formData.incidentId
              : null,
          applicationType: toBackendApplicationType(formData.applicationType),
          submitMode: ApplicationSubmitMode.Complete,
        },
      }),
      transformResponse: (res: SaveFormResponse) => {
        if ('signingUrl' in res) {
          return { statusCode: res.statusCode, signingUrl: res.signingUrl };
        } else {
          return { statusCode: res.statusCode };
        }
      },
      transformErrorResponse: (res) => {
        const payload = res.data;
        return {
          status: res.status,
          message: extractApiErrorMessage(payload),
        } as ApplicationSubmitError;
      },
    }),
  }),
});
export const {
  useGetStartTextsQuery,
  useLazyGetStartTextsQuery,
  useGetFullTextsQuery,
  useLazyGetFullTextsQuery,
  useGetLatestIncidentBySsnMutation,
  useGetApplicationDocumentMutation,
  useGetScriveConfirmationTextsQuery,
  useCreateApplicationMutation,
  useCompleteApplicationMutation,
} = applicationApi;
